import type { AiFredConversationId } from "@@/shared/alfred/ai/conversation-id";
import type { PromptMessage } from "@@/shared/alfred/ai/prompt-message";
import type { CreateTemplates } from "@@/shared/alfred/ai/tools/create-template";
import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import type { EntityState } from "@@/shared/entity-status";
import type { ProviderId } from "@@/shared/providers/provider-id";
import type { UserId } from "@@/shared/users/user-id";
import type { Except } from "type-fest";

type GPTModelType =
    | "gpt-4o"
    | "gpt-4o-mini"
    | "o3-mini"
    | "dall-e-3"
    | "dall-e-2";

const GPTModelTypes: GPTModelType[] = [
    "gpt-4o-mini",
    "o3-mini",
    "gpt-4o",
    "dall-e-3",
    "dall-e-2",
];

type AiFredConversationChat = {
    readonly _id: AiFredConversationId;
    readonly _type: "AIFRED_CONVERSATION";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _updatedAt: IsoAndUnixTimestamp;
    readonly _status: EntityState;

    //Conversation is a chat, a task is single task and should not be a chat
    readonly type: "CONVERSATION" | "TASK";

    readonly userId: UserId;
    readonly providerId?: ProviderId;
    readonly settings: {
        systemPrompt: string;
        model: GPTModelType;
    };

    readonly threadId?: string;

    readonly history: PromptMessage[];

    readonly toolResults: ToolResult[];
};

type AiFredConversationImage = {
    readonly _id: AiFredConversationId;
    readonly _type: "AIFRED_CONVERSATION";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _updatedAt: IsoAndUnixTimestamp;
    readonly _status: EntityState;

    //Conversation is a chat, a task is single task and should not be a chat
    readonly type: "TASK_GENERATE_IMAGE";

    readonly userId: UserId;
    readonly providerId?: ProviderId;
    readonly settings: {
        systemPrompt: string;
        model: "dall-e-3" | "dall-e-2";
    };
    readonly history: PromptMessage[];
};

type AiFredConversation = AiFredConversationChat | AiFredConversationImage;

type ToolResult = {
    result: CreateTemplates;
};

type FactoryParams = Except<
    AiFredConversation,
    "_type" | "_status" | "_createdAt"
>;

const aiFredConversationFactory = (params: FactoryParams) => {
    const AiFredConversation: AiFredConversation = {
        ...params,
        _createdAt: nowIsoAndUnix(),
        _type: "AIFRED_CONVERSATION",
        _status: "ACTIVE",
    };
    return AiFredConversation;
};

export { aiFredConversationFactory, GPTModelTypes };

export type { AiFredConversation, GPTModelType, ToolResult };
